import {
	DEFAULT_RETENTION_AUDIT_INTERVAL_HOURS,
	isValidRetentionAuditInterval,
	MAX_RETENTION_AUDIT_INTERVAL_HOURS,
	resolveRetentionAuditSchedule,
} from "@repo/common";
import { db } from "@repo/ui";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { CalendarClock, ChevronDown } from "lucide-react";
import { type ReactNode, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";

interface SettingsPanelProps {
	icon: ReactNode;
	title: string;
	description: string;
	children: ReactNode;
}

function SettingsPanel({
	icon,
	title,
	description,
	children,
}: SettingsPanelProps) {
	const [isOpen, setIsOpen] = useState(false);
	const contentId = useId();

	return (
		<Card className="gap-0 border-slate-200 shadow-sm">
			<CardHeader className="bg-slate-50/80 p-0">
				<button
					type="button"
					aria-expanded={isOpen}
					aria-controls={contentId}
					onClick={() => setIsOpen((open) => !open)}
					className="flex w-full items-center justify-between gap-4 rounded-xl px-6 py-5 text-left outline-none transition-colors hover:bg-slate-100/80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
				>
					<span className="min-w-0">
						<span className="flex items-center gap-2 font-semibold text-slate-900">
							<span aria-hidden="true">{icon}</span>
							{title}
						</span>
						<span className="mt-1.5 block text-sm font-normal text-slate-500">
							{description}
						</span>
					</span>
					<ChevronDown
						aria-hidden="true"
						className={`h-5 w-5 shrink-0 text-slate-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
					/>
				</button>
			</CardHeader>
			{isOpen ? (
				<div id={contentId} className="border-t border-slate-100">
					<CardContent className="space-y-6 pt-6">{children}</CardContent>
				</div>
			) : null}
		</Card>
	);
}

export function SettingsPage() {
	const [isSaving, setIsSaving] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [auditScheduledEnabled, setAuditScheduledEnabled] = useState(false);
	const [auditIntervalHours, setAuditIntervalHours] = useState<number | string>(
		DEFAULT_RETENTION_AUDIT_INTERVAL_HOURS,
	);
	const validAuditInterval = isValidRetentionAuditInterval(
		Number(auditIntervalHours),
	);
	const saveDisabled =
		isSaving || isLoading || loadError || !validAuditInterval;
	const [userUiEnabled, setUserUiEnabled] = useState(false);
	const [agentInjectionEnabled, setAgentInjectionEnabled] = useState(false);
	const [disabledForAll, setDisabledForAll] = useState(false);
	const [elevenLabsDefaultEnabled, setElevenLabsDefaultEnabled] =
		useState(false);
	const [outboundDefaultEnabled, setOutboundDefaultEnabled] = useState(true);
	const [outboundDisabledForAll, setOutboundDisabledForAll] = useState(false);
	const [outboundDispatcherEnabled, setOutboundDispatcherEnabled] =
		useState(false);
	const [outboundDispatcherCallsPerRun, setOutboundDispatcherCallsPerRun] =
		useState<number | string>(2);
	const [piiDefaultEnabled, setPiiDefaultEnabled] = useState(true);
	const [piiDisabledForAll, setPiiDisabledForAll] = useState(false);
	const [
		deleteAudioFromTwilioDefaultEnabled,
		setDeleteAudioFromTwilioDefaultEnabled,
	] = useState(false);
	const [
		deleteAudioFromTwilioDisabledForAll,
		setDeleteAudioFromTwilioDisabledForAll,
	] = useState(false);
	const [
		deletePiiJsonFromGcpDefaultEnabled,
		setDeletePiiJsonFromGcpDefaultEnabled,
	] = useState(false);
	const [
		deletePiiJsonFromGcpDisabledForAll,
		setDeletePiiJsonFromGcpDisabledForAll,
	] = useState(false);

	useEffect(() => {
		async function fetchSettings() {
			try {
				const snap = await getDoc(doc(db, "settings", "system"));
				if (snap.exists()) {
					const data = snap.data();
					const audit = resolveRetentionAuditSchedule(data);
					setAuditScheduledEnabled(audit.scheduledEnabled);
					setAuditIntervalHours(audit.intervalHours);
					setUserUiEnabled(data.callerMemory?.userUiEnabled === true);
					setAgentInjectionEnabled(
						data.callerMemory?.agentInjectionEnabled === true,
					);
					setDisabledForAll(data.callerMemory?.disabledForAll === true);
					setElevenLabsDefaultEnabled(
						data.elevenLabsProvider?.defaultEnabled === true,
					);
					setOutboundDefaultEnabled(
						data.outboundPlaybookCalls?.defaultEnabled !== false,
					);
					setOutboundDisabledForAll(
						data.outboundPlaybookCalls?.disabledForAll === true,
					);
					setOutboundDispatcherEnabled(
						data.outboundPlaybookCalls?.dispatcherEnabled === true,
					);
					setOutboundDispatcherCallsPerRun(
						typeof data.outboundPlaybookCalls?.dispatcherCallsPerRun ===
							"number" && data.outboundPlaybookCalls.dispatcherCallsPerRun > 0
							? data.outboundPlaybookCalls.dispatcherCallsPerRun
							: 2,
					);
					setPiiDefaultEnabled(data.piiRemoval?.defaultEnabled !== false);
					setPiiDisabledForAll(data.piiRemoval?.disabledForAll === true);
					setDeleteAudioFromTwilioDefaultEnabled(
						data.deleteAudioFromTwilio?.defaultEnabled === true,
					);
					setDeleteAudioFromTwilioDisabledForAll(
						data.deleteAudioFromTwilio?.disabledForAll === true,
					);
					setDeletePiiJsonFromGcpDefaultEnabled(
						data.deletePiiJsonFromGcp?.defaultEnabled === true,
					);
					setDeletePiiJsonFromGcpDisabledForAll(
						data.deletePiiJsonFromGcp?.disabledForAll === true,
					);
				}
			} catch (err) {
				setLoadError(true);
				console.error("Failed to load settings", err);
			} finally {
				setIsLoading(false);
			}
		}
		fetchSettings();
	}, []);

	const handleSaveFeatureFlags = async () => {
		if (saveDisabled) return;
		setIsSaving(true);
		try {
			const callsPerRunParsed = Math.max(
				1,
				Number(outboundDispatcherCallsPerRun) || 2,
			);
			await setDoc(
				doc(db, "settings", "system"),
				{
					retentionAudit: {
						scheduledEnabled: auditScheduledEnabled,
						intervalHours: Number(auditIntervalHours),
					},
					callerMemory: {
						userUiEnabled,
						agentInjectionEnabled,
						disabledForAll,
					},
					elevenLabsProvider: { defaultEnabled: elevenLabsDefaultEnabled },
					outboundPlaybookCalls: {
						defaultEnabled: outboundDefaultEnabled,
						disabledForAll: outboundDisabledForAll,
						dispatcherEnabled: outboundDispatcherEnabled,
						dispatcherCallsPerRun: callsPerRunParsed,
					},
					piiRemoval: {
						defaultEnabled: piiDefaultEnabled,
						disabledForAll: piiDisabledForAll,
					},
					deleteAudioFromTwilio: {
						defaultEnabled: deleteAudioFromTwilioDefaultEnabled,
						disabledForAll: deleteAudioFromTwilioDisabledForAll,
					},
					deletePiiJsonFromGcp: {
						defaultEnabled: deletePiiJsonFromGcpDefaultEnabled,
						disabledForAll: deletePiiJsonFromGcpDisabledForAll,
					},
				},
				{ merge: true },
			);
			setOutboundDispatcherCallsPerRun(callsPerRunParsed);
			toast.success("System settings updated successfully");
		} catch (err) {
			console.error(err);
			toast.error("Failed to save system settings");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className="space-y-6 max-w-5xl">
			<div className="flex items-center justify-between">
				<div>
					<h1>System Settings</h1>
					<p className="text-slate-600">
						Manage global feature flags, operational limits, and system
						controls.
					</p>
				</div>
				<Button onClick={handleSaveFeatureFlags} disabled={saveDisabled}>
					{isSaving ? "Saving..." : "Save All Settings"}
				</Button>
			</div>
			{loadError && (
				<p role="alert" className="text-sm text-destructive">
					Could not load system settings. Reload this page before saving.
				</p>
			)}

			<SettingsPanel
				icon={null}
				title="ElevenLabs Provider"
				description={
					elevenLabsDefaultEnabled
						? "Enabled by default. Companies can override this setting."
						: "Disabled by default. Companies can explicitly enable it."
				}
			>
				<div className="flex items-center justify-between gap-4">
					<div className="space-y-1">
						<Label htmlFor="elevenlabs-default">
							Enable ElevenLabs by default
						</Label>
						<p className="text-sm text-muted-foreground">
							Allow companies to select ElevenLabs for agents. Disabled
							companies use LiveKit and do not see provider options.
						</p>
					</div>
					<Switch
						id="elevenlabs-default"
						checked={elevenLabsDefaultEnabled}
						onCheckedChange={setElevenLabsDefaultEnabled}
					/>
				</div>
			</SettingsPanel>

			<SettingsPanel
				icon={<CalendarClock className="h-4 w-4" />}
				title="Retention Audits"
				description={
					auditScheduledEnabled
						? "Scheduled audits enabled. Review the frequency below."
						: "Scheduled audits disabled. Manual audit batches remain available."
				}
			>
				<div className="flex items-center justify-between gap-4">
					<div className="space-y-1">
						<Label htmlFor="audit-scheduled">Enable scheduled audits</Label>
						<p
							id="audit-scheduled-help"
							className="max-w-prose text-sm text-muted-foreground"
						>
							Off by default. You can run manual batches at any time from
							Diagnostics → Audit findings.
						</p>
					</div>
					<Switch
						id="audit-scheduled"
						aria-describedby="audit-scheduled-help"
						checked={auditScheduledEnabled}
						onCheckedChange={setAuditScheduledEnabled}
						disabled={isLoading || isSaving || loadError}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="audit-interval">Run every (hours)</Label>
					<Input
						id="audit-interval"
						type="number"
						min={1}
						max={MAX_RETENTION_AUDIT_INTERVAL_HOURS}
						step={1}
						className="max-w-32"
						value={auditIntervalHours}
						onChange={(event) => setAuditIntervalHours(event.target.value)}
						disabled={isLoading || isSaving || loadError}
						aria-invalid={!validAuditInterval}
						aria-describedby="audit-interval-help audit-interval-error"
					/>
					<p
						id="audit-interval-help"
						className="max-w-prose text-sm text-muted-foreground"
					>
						24 hours runs one batch per day. Each batch checks one scope of up
						to 25 records; it does not scan everything or perform cleanup.
						Discovery also advances hourly while scheduling is enabled; its
						budget is defined in code. Changes apply after saving.
					</p>
					{!validAuditInterval && (
						<p
							id="audit-interval-error"
							role="alert"
							className="text-sm text-destructive"
						>
							Enter a whole number from 1 to{" "}
							{MAX_RETENTION_AUDIT_INTERVAL_HOURS} hours.
						</p>
					)}
				</div>
			</SettingsPanel>

			<SettingsPanel
				icon="📞"
				title="Outbound Playbook Calls"
				description="Configure automated dispatching schedules, execution batch limits, and global visibility."
			>
				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							Automatic Task Dispatcher
						</Label>
						<p className="text-sm text-slate-500">
							Runs the outbound task dispatcher cron every 5 minutes.
							Company-level flags still apply.
						</p>
					</div>
					<Switch
						checked={outboundDispatcherEnabled}
						onCheckedChange={setOutboundDispatcherEnabled}
					/>
				</div>

				<Separator />

				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							Calls Per Dispatcher Run
						</Label>
						<p className="text-sm text-slate-500">
							Maximum number of outbound calls dispatched system-wide per
							5-minute scheduled execution cycle (default: 2).
						</p>
					</div>
					<Input
						type="number"
						min={1}
						className="w-24 text-right font-mono"
						value={outboundDispatcherCallsPerRun}
						onChange={(e) => setOutboundDispatcherCallsPerRun(e.target.value)}
					/>
				</div>

				<Separator />

				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							Default Enabled for Companies
						</Label>
						<p className="text-sm text-slate-500">
							Default setting for displaying outbound Playbook call UI and
							allowing task dispatch for companies without an explicit override.
						</p>
					</div>
					<Switch
						checked={outboundDefaultEnabled}
						onCheckedChange={setOutboundDefaultEnabled}
					/>
				</div>

				<Separator />

				<div className="flex items-center justify-between bg-red-50/50 p-3 rounded-lg border border-red-100">
					<div className="space-y-0.5">
						<Label className="text-base font-medium text-red-900">
							Emergency Kill Switch: Disable Outbound Calls For All
						</Label>
						<p className="text-sm text-red-600">
							When enabled, outbound Playbook calls are immediately hidden and
							dispatching is disabled for every company globally.
						</p>
					</div>
					<Switch
						checked={outboundDisabledForAll}
						onCheckedChange={setOutboundDisabledForAll}
					/>
				</div>
			</SettingsPanel>

			<SettingsPanel
				icon="🧠"
				title="Caller Memory"
				description="Configure caller memory UI display in Call Details, AI agent context injection, and global controls."
			>
				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							User UI Card Visibility
						</Label>
						<p className="text-sm text-slate-500">
							Enables the Caller Memory section inside the Call Details page for
							web application users.
						</p>
					</div>
					<Switch checked={userUiEnabled} onCheckedChange={setUserUiEnabled} />
				</div>

				<Separator />

				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							Agent Context Injection
						</Label>
						<p className="text-sm text-slate-500">
							Allows AI Agents (LiveKit) to retrieve semantic memory and inject
							relevant caller context into LLM system prompts.
						</p>
					</div>
					<Switch
						checked={agentInjectionEnabled}
						onCheckedChange={setAgentInjectionEnabled}
					/>
				</div>

				<Separator />

				<div className="flex items-center justify-between bg-red-50/50 p-3 rounded-lg border border-red-100">
					<div className="space-y-0.5">
						<Label className="text-base font-medium text-red-900">
							Emergency Kill Switch: Disable Caller Memory For All
						</Label>
						<p className="text-sm text-red-600">
							When enabled, caller memory extraction and retrieval is disabled
							for every company regardless of company overrides.
						</p>
					</div>
					<Switch
						checked={disabledForAll}
						onCheckedChange={setDisabledForAll}
					/>
				</div>
			</SettingsPanel>

			<SettingsPanel
				icon="🔒"
				title="PII Removal & Redaction"
				description="Configure automated detection, localized redaction, and audio timestamp extraction for credit cards, CVVs, and national IDs."
			>
				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							Default Enabled for All
						</Label>
						<p className="text-sm text-slate-500">
							When enabled, all companies without a specific override will
							automatically redact sensitive PII and save audio timestamps to
							Cloud Storage.
						</p>
					</div>
					<Switch
						checked={piiDefaultEnabled}
						onCheckedChange={setPiiDefaultEnabled}
					/>
				</div>

				<Separator />

				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							Delete Audio from Twilio
						</Label>
						<p className="text-sm text-slate-500">
							When enabled, original raw recordings are permanently deleted from
							Twilio servers once archived in Google Cloud Storage.
						</p>
					</div>
					<Switch
						checked={deleteAudioFromTwilioDefaultEnabled}
						onCheckedChange={setDeleteAudioFromTwilioDefaultEnabled}
					/>
				</div>

				<Separator />

				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label className="text-base font-medium">
							Delete pii.json from GCP
						</Label>
						<p className="text-sm text-slate-500">
							When enabled, the companion PII detection metadata JSON is
							permanently deleted from Google Cloud Storage after tone
							redaction.
						</p>
					</div>
					<Switch
						checked={deletePiiJsonFromGcpDefaultEnabled}
						onCheckedChange={setDeletePiiJsonFromGcpDefaultEnabled}
					/>
				</div>

				<Separator />

				<div className="flex items-center justify-between bg-red-50/50 p-3 rounded-lg border border-red-100">
					<div className="space-y-0.5">
						<Label className="text-base font-medium text-red-900">
							Emergency Kill Switch: Disable PII Removal For All
						</Label>
						<p className="text-sm text-red-600">
							When enabled, PII detection and redaction is disabled globally for
							all companies, overriding individual company settings.
						</p>
					</div>
					<Switch
						checked={piiDisabledForAll}
						onCheckedChange={setPiiDisabledForAll}
					/>
				</div>

				<div className="flex items-center justify-between bg-red-50/50 p-3 rounded-lg border border-red-100">
					<div className="space-y-0.5">
						<Label className="text-base font-medium text-red-900">
							Emergency Kill Switch: Disable Twilio Audio Deletion For All
						</Label>
						<p className="text-sm text-red-600">
							When enabled, prevents raw recording deletion from Twilio globally
							for all companies, overriding individual company settings.
						</p>
					</div>
					<Switch
						checked={deleteAudioFromTwilioDisabledForAll}
						onCheckedChange={setDeleteAudioFromTwilioDisabledForAll}
					/>
				</div>

				<div className="flex items-center justify-between bg-red-50/50 p-3 rounded-lg border border-red-100">
					<div className="space-y-0.5">
						<Label className="text-base font-medium text-red-900">
							Emergency Kill Switch: Disable GCP PII Sidecar Deletion For All
						</Label>
						<p className="text-sm text-red-600">
							When enabled, prevents PII JSON sidecar deletion from Cloud
							Storage globally for all companies, overriding individual company
							settings.
						</p>
					</div>
					<Switch
						checked={deletePiiJsonFromGcpDisabledForAll}
						onCheckedChange={setDeletePiiJsonFromGcpDisabledForAll}
					/>
				</div>
			</SettingsPanel>

			<div className="flex justify-end pt-2">
				<Button
					onClick={handleSaveFeatureFlags}
					disabled={saveDisabled}
					size="lg"
				>
					{isSaving ? "Saving Settings..." : "Save All Settings"}
				</Button>
			</div>
		</div>
	);
}
